import React, { useState } from 'react';
import { motion } from 'framer-motion';

const InteractiveFeaturesSection = () => {
  // State to track which feature is being hovered
  const [activeFeature, setActiveFeature] = useState(null);

  // Define the features data
  const features = [
    {
      id: 'fast',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
        </svg>
      ),
      title: 'Fast & Easy',
      description: 'Create videos in minutes',
      color: '#f26419', // brand orange
      animation: (
        <motion.div
          className="absolute inset-0 flex justify-center items-center"
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
        >
          <motion.div 
            className="h-7 w-1 rounded-full mx-0.5" style={{ background:"var(--c2v-gold)" }}
            initial={{ height: 10 }}
            animate={{ 
              height: [10, 28, 10],
              transition: { repeat: Infinity, duration: 1.2, ease: "easeInOut" }
            }}
          />
          <motion.div 
            className="h-7 w-1 rounded-full mx-0.5" style={{ background:"var(--c2v-primary)" }}
            initial={{ height: 15 }}
            animate={{ 
              height: [15, 28, 15],
              transition: { repeat: Infinity, duration: 1.2, delay: 0.2, ease: "easeInOut" }
            }}
          />
          <motion.div 
            className="h-7 w-1 rounded-full mx-0.5" style={{ background:"var(--c2v-primary-dark)" }}
            initial={{ height: 20 }}
            animate={{ 
              height: [20, 28, 20],
              transition: { repeat: Infinity, duration: 1.2, delay: 0.4, ease: "easeInOut" }
            }}
          />
        </motion.div>
      )
    },
    {
      id: 'types',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <line x1="9" y1="9" x2="9" y2="15" />
          <line x1="15" y1="9" x2="15" y2="15" />
          <line x1="3" y1="9" x2="21" y2="9" />
          <line x1="3" y1="15" x2="21" y2="15" />
        </svg>
      ),
      title: 'Multiple Chart Types',
      description: 'Bar, line, pie & more',
      color: '#f04e6b', // brand rose
      animation: (
        <motion.div
          className="absolute inset-0 flex justify-center items-center"
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
        >
          {/* Bar Chart */}
          <motion.div className="flex items-end h-7 space-x-1">
            <motion.div 
              className="w-1.5 rounded-t-sm" style={{ background:"var(--c2v-rose)" }}
              initial={{ height: 0 }}
              animate={{ 
                height: [0, 14, 14, 0],
                transition: { duration: 2, repeat: Infinity, repeatDelay: 1, ease: "easeOut" }
              }}
            />
            <motion.div 
              className="w-1.5 rounded-t-sm" style={{ background:"var(--c2v-rose)" }}
              initial={{ height: 0 }}
              animate={{ 
                height: [0, 20, 20, 0],
                transition: { duration: 2, repeat: Infinity, repeatDelay: 1, delay: 0.1, ease: "easeOut" }
              }}
            />
            <motion.div 
              className="w-1.5 rounded-t-sm" style={{ background:"var(--c2v-rose)" }}
              initial={{ height: 0 }}
              animate={{ 
                height: [0, 16, 16, 0],
                transition: { duration: 2, repeat: Infinity, repeatDelay: 1, delay: 0.2, ease: "easeOut" }
              }}
            />
            <motion.div 
              className="w-1.5 rounded-t-sm" style={{ background:"var(--c2v-rose)" }}
              initial={{ height: 0 }}
              animate={{ 
                height: [0, 25, 25, 0],
                transition: { duration: 2, repeat: Infinity, repeatDelay: 1, delay: 0.3, ease: "easeOut" }
              }}
            />
            <motion.div 
              className="w-1.5 rounded-t-sm" style={{ background:"var(--c2v-rose)" }}
              initial={{ height: 0 }}
              animate={{ 
                height: [0, 18, 18, 0],
                transition: { duration: 2, repeat: Infinity, repeatDelay: 1, delay: 0.4, ease: "easeOut" }
              }}
            />
          </motion.div>

          {/* Pie Chart (appears after bar chart) */}
          <motion.div 
            className="absolute inset-0 flex justify-center items-center"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ 
              opacity: [0, 0, 1, 1, 0],
              scale: [0.5, 0.5, 1, 1, 0.5],
              transition: { 
                duration: 3, 
                times: [0, 0.4, 0.5, 0.9, 1],
                repeat: Infinity,
                delay: 3.5,
                repeatDelay: 0.5
              }
            }}
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
              <motion.path
                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"
                fill="var(--c2v-rose)"
                initial={{ pathLength: 0 }}
                animate={{ 
                  pathLength: 1,
                  transition: { duration: 1, ease: "easeInOut" }
                }}
              />
              <motion.path
                d="M12 2v10l8.6 5"
                stroke="var(--c2v-rose)"
                strokeWidth="2"
                fill="none"
                initial={{ pathLength: 0 }}
                animate={{ 
                  pathLength: 1,
                  transition: { duration: 1, delay: 0.5, ease: "easeInOut" }
                }}
              />
            </svg>
          </motion.div>

          {/* Line Chart (appears last) */}
          <motion.div 
            className="absolute inset-0 flex justify-center items-center"
            initial={{ opacity: 0 }}
            animate={{ 
              opacity: [0, 0, 1, 1, 0],
              transition: { 
                duration: 3, 
                times: [0, 0.6, 0.7, 0.95, 1],
                repeat: Infinity,
                delay: 7,
                repeatDelay: 0.5
              }
            }}
          >
            <svg width="30" height="20" viewBox="0 0 30 20" fill="none">
              <motion.path
                d="M0 15 L5 12 L10 16 L15 8 L20 10 L25 4 L30 7"
                stroke="var(--c2v-rose)"
                strokeWidth="2"
                fill="none"
                initial={{ pathLength: 0 }}
                animate={{ 
                  pathLength: 1,
                  transition: { duration: 1.5, ease: "easeInOut" }
                }}
              />
            </svg>
          </motion.div>
        </motion.div>
      )
    },
    {
      id: 'customizable',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <path d="M20.4 14.5L16 10 4 20" />
        </svg>
      ),
      title: 'Customizable',
      description: 'Colors, animations & more',
      color: '#10B981', // Green
      animation: (
        <motion.div
          className="absolute inset-0 flex justify-center items-center"
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
        >
          {/* Color palette dots */}
          <div className="relative h-8 w-8">
            <motion.div 
              className="absolute rounded-full h-3 w-3 bg-red-500"
              initial={{ x: 0, y: 0 }}
              animate={{ 
                x: [0, -10, -10, 0, 0],
                y: [0, -10, -10, 0, 0],
                transition: { duration: 3, repeat: Infinity }
              }}
            />
            <motion.div 
              className="absolute rounded-full h-3 w-3 bg-blue-500"
              initial={{ x: 0, y: 0 }}
              animate={{ 
                x: [0, 10, 10, 0, 0],
                y: [0, -10, -10, 0, 0],
                transition: { duration: 3, repeat: Infinity, delay: 0.1 }
              }}
            />
            <motion.div 
              className="absolute rounded-full h-3 w-3 bg-yellow-500"
              initial={{ x: 0, y: 0 }}
              animate={{ 
                x: [0, -10, -10, 0, 0],
                y: [0, 10, 10, 0, 0],
                transition: { duration: 3, repeat: Infinity, delay: 0.2 }
              }}
            />
            <motion.div 
              className="absolute rounded-full h-3 w-3" style={{ background:"var(--c2v-accent)" }}
              initial={{ x: 0, y: 0 }}
              animate={{ 
                x: [0, 10, 10, 0, 0],
                y: [0, 10, 10, 0, 0],
                transition: { duration: 3, repeat: Infinity, delay: 0.3 }
              }}
            />
          </div>
        </motion.div>
      )
    },
    {
      id: 'export',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
      ),
      title: 'Export Options',
      description: 'MP4 or JPG',
      color: '#F59E0B', // Amber
      animation: (
        <motion.div
          className="absolute inset-0 flex justify-center items-center"
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
        >
          <div className="relative h-8 w-8 flex justify-center items-center">
            {/* MP4 */}
            <motion.div 
              className="absolute flex justify-center items-center"
              initial={{ opacity: 0, y: 10 }}
              animate={{ 
                opacity: [0, 1, 1, 0],
                y: [10, 0, 0, -10],
                transition: { 
                  duration: 2.5, 
                  times: [0, 0.2, 0.8, 1],
                  repeat: Infinity,
                  repeatDelay: 0.5
                }
              }}
            >
              <div className="text-xs font-bold text-amber-500">MP4</div>
            </motion.div>
            
            {/* GIF */}
            <motion.div 
              className="absolute flex justify-center items-center"
              initial={{ opacity: 0, y: 10 }}
              animate={{ 
                opacity: [0, 1, 1, 0],
                y: [10, 0, 0, -10],
                transition: { 
                  duration: 2.5, 
                  times: [0, 0.2, 0.8, 1],
                  repeat: Infinity,
                  repeatDelay: 0.5,
                  delay: 1
                }
              }}
            >
              <div className="text-xs font-bold text-amber-500">GIF</div>
            </motion.div>
            
            {/* WebM */}
            <motion.div 
              className="absolute flex justify-center items-center"
              initial={{ opacity: 0, y: 10 }}
              animate={{ 
                opacity: [0, 1, 1, 0],
                y: [10, 0, 0, -10],
                transition: { 
                  duration: 2.5, 
                  times: [0, 0.2, 0.8, 1],
                  repeat: Infinity,
                  repeatDelay: 0.5,
                  delay: 2
                }
              }}
            >
              <div className="text-xs font-bold text-amber-500">WebM</div>
            </motion.div>
          </div>
        </motion.div>
      )
    }
  ];

  return (
    <section className="py-8 bg-white border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {features.map((feature) => (
            <motion.div
              key={feature.id}
              className="flex flex-col items-center text-center relative"
              onHoverStart={() => setActiveFeature(feature.id)}
              onHoverEnd={() => setActiveFeature(null)}
              whileHover={{ y: -5 }}
              transition={{ type: 'spring', stiffness: 300 }}
            >
              <motion.div 
                className="w-16 h-16 rounded-full flex items-center justify-center mb-3 relative overflow-hidden" style={{ background:"var(--c2v-primary-light)" }}
                whileHover={{ 
                  scale: 1.1,
                  backgroundColor: feature.color + '20', // Add transparency
                  boxShadow: '0 0 15px ' + feature.color + '40'
                }}
              >
                {/* Static icon that fades out on hover */}
                <motion.div 
                  className="z-10" style={{ color:"var(--c2v-primary)" }}
                  animate={{ 
                    opacity: activeFeature === feature.id ? 0 : 0.3,
                    scale: activeFeature === feature.id ? 0.8 : 1,
                  }}
                  transition={{ duration: 0.3 }}
                >
                  {feature.icon}
                </motion.div>

                {/* Animation is always visible, but gets emphasized on hover */}
                <motion.div
                  className="absolute inset-0 flex items-center justify-center"
                  animate={{ 
                    scale: activeFeature === feature.id ? 1.1 : 1,
                    opacity: activeFeature === feature.id ? 1 : 0.8,
                  }}
                  transition={{ duration: 0.3 }}
                >
                  {feature.animation}
                </motion.div>
              </motion.div>

              <motion.h3 
                className="text-sm font-semibold text-gray-900"
                whileHover={{ color: feature.color }}
              >
                {feature.title}
              </motion.h3>
              
              <motion.p 
                className="text-xs text-gray-600"
                animate={{ 
                  color: activeFeature === feature.id ? feature.color : '#4B5563'
                }}
                transition={{ duration: 0.3 }}
              >
                {feature.description}
              </motion.p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default InteractiveFeaturesSection;